import { REWRITE_TIERS } from '@lyrmo/shared';
import { matchesShortcut, versionFromKeyPress } from '@lyrmo/shared/shortcuts';
import { useEffect, useEffectEvent } from 'react';
import { useShortcuts } from '../shortcuts/useShortcuts';

interface RewriteShortcuts {
  readonly enabled?: boolean;
  readonly onSubmit: () => void;
  readonly onClear: () => void;
  /** Copies the version at `index`. */
  readonly onCopy: (index: number) => void;
  readonly onShowShortcuts: () => void;
  readonly onToggleFullScreen: () => void;
  /** Leaves full screen; omitted while the editor isn't full screen. */
  readonly onExitFullScreen?: (() => void) | undefined;
  /** Saves the draft, with the system's Save keys; omitted where drafts aren't saved. */
  readonly onSave?: (() => void) | undefined;
}

/** Save is the system's own, the same everywhere: ⌘S on a Mac, Ctrl+S elsewhere. */
export const SAVE_KEYS = 'Mod+S';

/**
 * The editor's shortcuts, with the keys the writer chose. Escape closes whatever is open, such as
 * a suggestion or a menu, and then full screen; it never clears the draft.
 */
export function useRewriteShortcuts({
  enabled = true,
  onSubmit,
  onClear,
  onCopy,
  onShowShortcuts,
  onToggleFullScreen,
  onExitFullScreen,
  onSave,
}: RewriteShortcuts): void {
  const { platform, shortcuts } = useShortcuts();

  const handleKeyDown = useEffectEvent((event: KeyboardEvent) => {
    if (!enabled) return;
    if (event.defaultPrevented || event.isComposing || event.repeat) return;
    if (event.target instanceof Element && event.target.closest('dialog')) return;
    const version = versionFromKeyPress(
      event,
      shortcuts.useVersion,
      platform,
      REWRITE_TIERS.length,
    );
    const action =
      version !== null
        ? () => {
            onCopy(version);
          }
        : matchesShortcut(event, shortcuts.rewrite, platform)
          ? onSubmit
          : matchesShortcut(event, shortcuts.clearDraft, platform)
            ? onClear
            : matchesShortcut(event, shortcuts.fullScreen, platform)
              ? onToggleFullScreen
              : matchesShortcut(event, shortcuts.showShortcuts, platform)
                ? onShowShortcuts
                : onSave && matchesShortcut(event, SAVE_KEYS, platform)
                  ? onSave
                  : // Menus and suggestions take their Escape first, and stop it.
                    event.key === 'Escape'
                    ? (onExitFullScreen ?? null)
                    : null;
    if (!action) return;
    event.preventDefault();
    action();
  });

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      handleKeyDown(event);
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, []);
}
